import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import styles from './Tabs.module.css';

/* ------------------------------------------------------------------ */
/*  Internal context — tracks trigger elements for indicator layout   */
/* ------------------------------------------------------------------ */

interface TabsContextValue {
  registerTrigger: (value: string, el: HTMLButtonElement | null) => void;
  value: string;
}

const TabsContext = createContext<TabsContextValue | null>(null);

function useTabsContext() {
  const ctx = useContext(TabsContext);
  if (!ctx)
    throw new Error('Tabs compound components must be used within <Tabs>');
  return ctx;
}

/* ------------------------------------------------------------------ */
/*  Tabs (Root)                                                       */
/* ------------------------------------------------------------------ */

interface TabsProps {
  value: string;
  onValueChange: (value: string) => void;
  children: ReactNode;
}

export function Tabs({ value, onValueChange, children }: TabsProps) {
  const triggersRef = useRef(new Map<string, HTMLButtonElement>());

  const registerTrigger = useCallback(
    (triggerValue: string, el: HTMLButtonElement | null) => {
      if (el) {
        triggersRef.current.set(triggerValue, el);
      } else {
        triggersRef.current.delete(triggerValue);
      }
    },
    []
  );

  const ctx = useMemo<TabsContextValue>(
    () => ({ registerTrigger, value }),
    [registerTrigger, value]
  );

  return (
    <TabsContext.Provider value={ctx}>
      <TabsPrimitive.Root
        value={value}
        onValueChange={onValueChange}
        className={styles.root}
      >
        {children}
      </TabsPrimitive.Root>
    </TabsContext.Provider>
  );
}

/* ------------------------------------------------------------------ */
/*  TabsList — contains triggers + animated indicator                 */
/* ------------------------------------------------------------------ */

interface TabsListProps {
  children: ReactNode;
  /** Optional extra class (e.g. for Reader worklist justified tabs) */
  className?: string;
}

export function TabsList({ children, className }: TabsListProps) {
  const { value } = useTabsContext();
  const listRef = useRef<HTMLDivElement>(null);
  const [indicator, setIndicator] = useState({ left: 0, width: 0 });
  const [ready, setReady] = useState(false);

  const measure = useCallback(() => {
    const list = listRef.current;
    if (!list) return;

    const activeEl = list.querySelector<HTMLButtonElement>(
      '[data-state="active"]'
    );
    if (!activeEl) return;

    const listRect = list.getBoundingClientRect();
    const tabRect = activeEl.getBoundingClientRect();

    setIndicator({
      left: tabRect.left - listRect.left,
      width: tabRect.width,
    });
    setReady(true);
  }, []);

  // Re-measure when value changes
  useLayoutEffect(() => {
    measure();
  }, [value, measure]);

  // Re-measure on container resize
  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list) return undefined;

    const ro = new ResizeObserver(() => measure());
    ro.observe(list);
    return () => ro.disconnect();
  }, [measure]);

  const listClassName = className ? `${styles.list} ${className}` : styles.list;
  return (
    <TabsPrimitive.List ref={listRef} className={listClassName}>
      {children}
      <div
        className={styles.indicator}
        style={{
          transform: `translateX(${indicator.left}px)`,
          width: indicator.width,
          opacity: ready ? 1 : 0,
        }}
      />
    </TabsPrimitive.List>
  );
}

/* ------------------------------------------------------------------ */
/*  TabsTrigger — individual tab button                               */
/* ------------------------------------------------------------------ */

interface TabsTriggerProps {
  value: string;
  children: ReactNode;
  badge?: number;
  icon?: ReactNode;
}

export function TabsTrigger({
  value,
  children,
  badge,
  icon,
}: TabsTriggerProps) {
  const { registerTrigger } = useTabsContext();

  const callbackRef = useCallback(
    (el: HTMLButtonElement | null) => {
      registerTrigger(value, el);
    },
    [registerTrigger, value]
  );

  return (
    <TabsPrimitive.Trigger
      ref={callbackRef}
      value={value}
      className={styles.trigger}
    >
      {icon && <span className={styles.icon}>{icon}</span>}
      {children}
      {badge != null && (
        <span
          className={`${styles.badge} ${badge === 0 ? styles.badgeDimmed : ''}`}
        >
          {badge}
        </span>
      )}
    </TabsPrimitive.Trigger>
  );
}
